import { compact, flattenDeep, isEmpty, uniq } from "lodash";
import { Base } from "../base";
import { Currency, DEFAULT_CURRENCY } from "../currency";
import { DEFAULT_LANGUAGE, Language, Translation } from "../language";
import { Network } from "../network";
import { AirdropConfig, NFTStandard, toNFTId } from "../nft";
import { DEFAULT_TRANSACTION_FEE, PaymentMethod } from "../payment";
import { MS_END, Timezone } from "../timezone";
import {
  cryptoId,
  normalizeId,
  toDuration,
  toJSON,
  toSearchText,
  unique,
} from "../utils";
import { NFTEventTier, NFTEventTierParam } from "./tier";

export * from "./constants";

export interface NFTEventOrganizer {
  name?: string;
  url?: string;
  logo?: string;
  description?: string;
}

export enum EventStatus {
  DRAFT = "draft",
  PUBLISHED = "published",
  DELETED = "deleted",
  HIDDEN = "hidden",
}

export enum EventType {
  TICKET = "ticket",
  BENEFIT = "benefit",
  MEMBERSHIP = "membership",
}

export interface NFTEventInfoParam extends Partial<NFTEventInfo> {}

export class NFTEventInfo {
  name: string;
  descriptions: { title: string; content: string }[];
  organizer: NFTEventOrganizer;
  location?: string;
  locationQuery?: string;
  locationUrl?: string;
  date?: string;
  time?: string;
  banner?: string;
  hero?: string;
  gallery?: string[];
  externalLinks?: { name: string; url: string }[];
  faqs?: { question: string; answer: string }[];
  customActions?: { name: string; url: string }[];
  url: string;
  subtitle?: string;

  constructor(params?: NFTEventInfoParam) {
    this.name = params?.name || "";
    this.descriptions = params?.descriptions || [];
    this.organizer = params?.organizer || { name: "", description: "" };
    this.url = params?.url || "";
    if (params?.date) this.date = params.date;
    if (params?.time) this.time = params.time;
    if (params?.location) this.location = params.location;
    if (params?.locationQuery) this.locationQuery = params.locationQuery;
    if (params?.locationUrl) this.locationUrl = params.locationUrl;
    if (params?.banner) this.banner = params.banner;
    if (params?.hero) this.hero = params.hero;
    if (params?.gallery) this.gallery = params.gallery;
    if (params?.externalLinks) this.externalLinks = params.externalLinks;
    if (params?.faqs) this.faqs = params.faqs;
    if (params?.customActions) this.customActions = params.customActions;
    if (params?.subtitle) this.subtitle = params.subtitle;
  }

  get json() {
    return toJSON(this);
  }

  get searchText(): string {
    return toSearchText(
      flattenDeep([
        this.name,
        this.organizer.name || "",
        this.location || "",
        ...this.descriptions.map((d) => [d.title || "", d.content || ""]),
      ]).join(" ")
    );
  }
}

export interface NFTEventCustomization {
  showCountdown?: boolean;
  redirectAfterSuccessUrl?: string;
  redirectAfterSuccessTimeout?: number;
}

export interface NFTEventParam
  extends Omit<Partial<NFTEvent>, "tiers" | "info"> {
  organizerId: string;
  info?: Translation<NFTEventInfoParam>;
  tiers?: NFTEventTierParam[];
  published?: boolean; // deprecated
  hidden?: boolean; // deprecated
}

export class NFTEvent extends Base implements NFTEventParam {
  type: EventType;
  organizerId: string;
  editors: string[];
  tiers: NFTEventTier[];
  networks: Network[];
  collections: string[];
  start: number;
  end: number;
  timezone: Timezone;
  tags?: string[];
  info: Translation<NFTEventInfo>;
  slug?: string;
  domain?: string;
  code: string;
  // Payment Configs
  currency?: Currency;
  paymentMethods?: PaymentMethod[];
  transactionFee?: number;
  allowCrossTierPurchase?: boolean;
  status: EventStatus;

  customizations?: NFTEventCustomization;
  allowBulkPurchase?: boolean;

  //@deprecated
  projectId?: string;
  version?: number;

  constructor(params: NFTEventParam) {
    super(params);
    this.organizerId = params.organizerId;
    this.editors = params.editors || [];

    if (params.slug) this.slug = normalizeId(params.slug);
    if (params.domain) this.domain = normalizeId(params.domain);
    this.code = normalizeId(params.code || cryptoId(4));

    this.info = {};
    if (!isEmpty(params.info)) {
      for (const language of Object.keys(params.info)) {
        const lang = language as Language;
        if (params.info[lang])
          this.info[lang] = new NFTEventInfo(params.info[lang]!);
      }
    } else {
      this.info[DEFAULT_LANGUAGE] = new NFTEventInfo({
        name: "",
      });
    }

    this.start = Number(params.start ?? 0);
    this.end = Number(params.end ?? MS_END);
    this.timezone = params.timezone || Timezone["Asia/Hong_Kong"];
    this.tiers =
      params.tiers
        ?.map((tier) => {
          if (tier.paymentConfig?.autoDiscounts) {
            tier.paymentConfig.autoDiscounts.forEach((config) => {
              config.discount.eventId = this.id;
            });
          }
          return new NFTEventTier(tier);
        })
        .sort((a, b) => a.order - b.order) || [];
    this.networks = unique(
      flattenDeep(
        this.tiers.map(
          (c) => c.nftConstraint?.collections.map((n) => n.network) || null
        ) as any
      )
    );
    this.collections = unique(
      flattenDeep(
        this.tiers.map(
          (tier) =>
            tier.nftConstraint?.collections.map((n) =>
              toNFTId([n.network, n.collectionId])
            ) || null
        ) as any
      )
    );
    this.tags = params.tags || [];
    if (params.version) this.version = params.version; //deprecated
    if (params.projectId) this.projectId = params.projectId; //@deprecated

    if (params.customizations) {
      this.customizations = params.customizations;
    }
    this.status = params.published // backward compatible
      ? EventStatus.PUBLISHED
      : params.hidden
      ? EventStatus.HIDDEN
      : params.status || EventStatus.DRAFT;

    // isPaid mean need to go through checkout
    if (this.isPaid) {
      // Initialize payment settings
      this.paymentMethods = !isEmpty(params.paymentMethods)
        ? params.paymentMethods
        : [PaymentMethod.STRIPE];
      this.transactionFee = Number(
        params.transactionFee ?? DEFAULT_TRANSACTION_FEE
      );
      this.currency = params.currency || DEFAULT_CURRENCY;
    } else {
      if (params.paymentMethods && !isEmpty(params.paymentMethods))
        this.paymentMethods = params.paymentMethods;
      if (params.transactionFee)
        this.transactionFee = Number(params.transactionFee);

      if (params.currency) this.currency = params.currency;
    }

    if (params.allowCrossTierPurchase) this.allowCrossTierPurchase = true;
    if (params.allowBulkPurchase) this.allowBulkPurchase = true;

    this.type =
      params.type ||
      (this.isWhitelisted ? EventType.BENEFIT : EventType.TICKET);
  }

  get json() {
    return toJSON(this);
  }

  get published() {
    return this.status === EventStatus.PUBLISHED;
  }

  get hidden() {
    return this.status === EventStatus.HIDDEN;
  }

  get duration(): string {
    return toDuration(this.start, this.end, this.timezone);
  }

  get isWhitelisted(): boolean {
    return this.tiers.some((tier) => tier.whitelistConstraint);
  }

  get isTokenGated(): boolean {
    return this.tiers.some((tier) => tier.nftConstraint);
  }

  get isMoonpassGated(): boolean {
    return this.tiers.some((tier) => tier.moonpassConstraint);
  }

  get isPublic(): boolean {
    return !(this.isWhitelisted || this.isTokenGated || this.isMoonpassGated);
  }

  // isPaid mean need to go through checkout
  get isPaid(): boolean {
    return this.tiers.some((tier) => tier.paymentConfig);
  }

  get hasUsageLimit(): boolean {
    return this.tiers.some(
      (tier) =>
        tier.usageConstraint &&
        (tier.usageConstraint.limit > 0 || tier.usageConstraint.boundToUser)
    );
  }

  get hasAirdrop(): boolean {
    return this.tiers.some(
      (tier) => tier.airdropConfig && tier.airdropConfig.contractId
    );
  }

  get hasAttendanceAirdrop(): boolean {
    return this.tiers.some(
      (tier) =>
        tier.attendanceAirdropConfig && tier.attendanceAirdropConfig.contractId
    );
  }

  get hasJobs(): boolean {
    return this.tiers.some(
      (tier) => tier.runnerConfig && !isEmpty(tier.runnerConfig.jobs)
    );
  }

  get isStarted(): boolean {
    return new Date().valueOf() >= this.start;
  }

  get isEnded(): boolean {
    return new Date().valueOf() > this.end;
  }

  get isERC1155BulkPurchase(): boolean {
    return !!(
      this.allowBulkPurchase &&
      this.tiers.some(
        (tier) => tier.airdropConfig?.standard === NFTStandard.ERC1155
      )
    );
  }

  get searchText(): string {
    return toSearchText(
      unique([
        ...(this.tags || []),
        ...Object.keys(this.info).map((language) => {
          const lang = language as Language;
          return this.info[lang]?.searchText || "";
        }),
      ]).join(" ")
    );
  }

  get airdropConfig(): AirdropConfig | null {
    const tier = this.tiers.find(
      (tier) => tier.airdropConfig && tier.airdropConfig.contractId
    );
    if (!tier?.airdropConfig) return null;
    return tier.airdropConfig;
  }

  validate() {
    const now = new Date().valueOf();
    if (now < this.start) return false;
    if (now > this.end) return false;
    return true;
  }

  updateCollections() {
    this.collections = uniq(
      compact(
        flattenDeep(
          this.tiers.map(
            (tier) =>
              tier.nftConstraint?.collections.map((n) =>
                toNFTId([n.network, n.collectionId])
              ) || null
          ) as any
        )
      )
    );
  }

  updateNetworks() {
    this.networks = uniq(
      compact(
        flattenDeep(
          this.tiers.map(
            (c) => c.nftConstraint?.collections.map((n) => n.network) || null
          ) as any
        )
      )
    );
  }

  update() {
    this.updateCollections();
    this.updateNetworks();
    this.updatedAt = new Date().valueOf();
  }

  addTier(constraint: NFTEventTier) {
    this.tiers.push(constraint);
    this.update();
  }

  removeTier(constraintId: string) {
    this.tiers = this.tiers.filter((c) => c.id != constraintId);
    this.update();
  }

  localeInfo(language: Language = DEFAULT_LANGUAGE): NFTEventInfo {
    if (language in this.info) return this.info[language]!;
    return this.info[DEFAULT_LANGUAGE] || new NFTEventInfo();
  }

  toURL(host = "https://app.moongate.id", https = true) {
    return this.domain
      ? `${https ? "https" : "http"}://${this.domain}`
      : this.localeInfo().url || `${host}/e/${this.slug || this.id}`;
  }
}
